import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from '@/components/ui';
import ParentWidget from './ParentWidget';
import type { InsuranceExpiryRow } from '../../services/vendorService';

/** What each row's `kind` is called on screen. */
const KIND_LABELS: Record<InsuranceExpiryRow['kind'], string> = {
  WCB: 'WCB clearance',
  LIABILITY_INSURANCE: 'Liability insurance',
  VEHICLE_INSURANCE: 'Vehicle insurance',
};

/**
 * "in 3 days" / "today" / "12 days overdue".
 *
 * Overdue reads as a positive count of days overdue rather than "-12 days": the sign is easy
 * to miss in a dense column, and the word is not.
 */
export function formatDaysUntilDue(days: number): string {
  if (days < 0) {
    const overdue = Math.abs(days);
    return `${overdue} ${overdue === 1 ? 'day' : 'days'} overdue`;
  }
  if (days === 0) return 'today';
  return `in ${days} ${days === 1 ? 'day' : 'days'}`;
}

/**
 * Where a row drills through to.
 *
 * A certificate is on the vendor's Documents tab; a machine's cover is on the resource, which
 * lives under Resources & Rates. Landing on the vendor's default tab would leave the reader
 * hunting for the thing they just clicked, so the tab is named explicitly.
 */
export function drillThroughPath(row: InsuranceExpiryRow): string {
  const tab = row.kind === 'VEHICLE_INSURANCE' ? 'resources' : 'documents';
  return `/vendors/${row.vendorId}?tab=${tab}`;
}

export interface InsuranceExpiryWidgetProps {
  id: string;
  title: string;
  rows: InsuranceExpiryRow[];
  windowDays: number;
  metadata?: { loading?: boolean; error?: Error | null; lastUpdated?: Date | null };
  onRefresh?: () => void;
  requiredPermission: string;
}

/**
 * HE-M18: vendor insurance and clearances due soon, most urgent first.
 *
 * A short scrollable table rather than a count, because the useful thing is which vendor to
 * chase, not how many. The list is capped in height so a long backlog cannot push the rest of
 * the dashboard off the screen; the count in the header says how much is below the fold.
 *
 * Rows arrive sorted by the server, so nothing is re-sorted here - one ordering, and it is the
 * one the API contract promises.
 */
const InsuranceExpiryWidget: React.FC<InsuranceExpiryWidgetProps> = ({
  id,
  title,
  rows,
  windowDays,
  metadata,
  onRefresh,
  requiredPermission,
}) => {
  const navigate = useNavigate();

  return (
  <ParentWidget<InsuranceExpiryRow[]>
    id={id}
    title={title}
    size="large"
    data={rows}
    metadata={metadata}
    onRefresh={onRefresh}
    requiredPermission={requiredPermission}
    isEmpty={(data) => !data || data.length === 0}
    headerExtra={
      rows.length > 0 ? (
        <span className="tw-text-xs tw-text-content-muted">
          {rows.length} due within {windowDays} days
        </span>
      ) : undefined
    }
  >
    {({ data }) => (
      // The whole card must stay under half the screen, so the scroll area is 50vh less an
      // allowance for the widget's own header and padding - capping the list at a flat 50vh
      // would put the card itself over.
      <div className="tw-max-h-[calc(50vh-6rem)] tw-overflow-y-auto">
        <Table hover size="sm" className="tw-mb-0">
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Document / resource</th>
              <th className="tw-text-right">Due</th>
            </tr>
          </thead>
          <tbody>
            {(data ?? []).map((row) => (
              <tr
                key={`${row.kind}-${row.resourceId ?? row.vendorId}-${row.subject}`}
                onClick={() => navigate(drillThroughPath(row))}
                className="tw-cursor-pointer"
                title={`Open ${row.vendorName}`}
              >
                <td className="tw-align-top">{row.vendorName}</td>
                <td className="tw-align-top">
                  <div>{row.subject}</div>
                  <div className="tw-text-xs tw-text-content-muted">{KIND_LABELS[row.kind]}</div>
                </td>
                <td
                  className={`tw-align-top tw-whitespace-nowrap tw-text-right ${
                    row.daysUntilDue < 0 ? 'tw-text-danger tw-font-medium' : ''
                  }`}
                >
                  {formatDaysUntilDue(row.daysUntilDue)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    )}
    </ParentWidget>
  );
};

export default InsuranceExpiryWidget;
